import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { GlassCard, Button } from '../components/ui';

interface SuccessState {
  formType: string;
  ticketId?: string;
  title: string;
  /** Team named in the "what happens next" list. Falls back to a guess from formType. */
  reviewTeam?: string;
}

export function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as SuccessState | null;

  if (!state) {
    return <Navigate to="/portal" replace />;
  }

  const reviewTeam =
    state.reviewTeam ?? (/^IT\b/.test(state.formType) ? 'IT team' : 'marketing team');

  return (
    <div className="min-h-screen flex flex-col w-full min-w-0">
      <Header />

      <main className="flex-1 w-full min-w-0 container mx-auto px-4 py-8 flex items-center justify-center max-w-full">
        <GlassCard
          className="p-8 md:p-12 max-w-lg w-full text-center"
          hoverable={false}
        >
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-bounce">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Request Submitted!
          </h1>

          <p className="text-gray-600 mb-6">
            Your {state.formType} has been successfully submitted.
          </p>

          <div className="bg-gray-50 border border-border rounded-xl p-4 mb-8 text-left">
            <h2 className="text-sm font-medium text-gray-500 mb-2">
              Request Details
            </h2>
            <p className="text-gray-900 font-medium">{state.title}</p>
            {state.ticketId && (
              <p className="text-gray-600 text-sm mt-2">
                Reference ID: {state.ticketId}
              </p>
            )}
          </div>

          <div className="text-left mb-8">
            <h2 className="text-sm font-medium text-gray-500 mb-2">
              What happens next?
            </h2>
            <ul className="text-gray-600 text-sm space-y-2">
              <li>• You will receive a confirmation email shortly</li>
              <li>• The {reviewTeam} will review your request</li>
              <li>• You&apos;ll be contacted if additional information is needed</li>
            </ul>
          </div>

          <Button onClick={() => navigate('/portal')} fullWidth>
            Return to Homepage
          </Button>
        </GlassCard>
      </main>

      <Footer />
    </div>
  );
}
